import React, { useEffect, useState } from 'react';
import { Button, ClipboardUtil, TextareaContentUtil, Tooltip } from '@axiom/ui';

import type { ChatEmailDraft } from '../../types/chat-email.types';
import { ChatEmailUtil } from '../../utils/chat-email-util';

const CONFIRMED_MS = 4000;

const TOOLTIPS = {
  body: {
    idle: 'Open a new email with TO: and SUBJECT: prefilled. BODY is copied to clipboard for quick pasting and editing.',
    started: 'Email opened. The body is on your clipboard - paste it in.',
  },
  noBody: {
    idle: 'Open a new email with TO: and SUBJECT: prefilled.',
    started: 'Email opened.',
  },
};

export type ChatEmailStartButtonProps = {
  bodyRef: React.RefObject<HTMLDivElement>;
  email: ChatEmailDraft;
  suffix: string;
  onBodyCopy: ({ copied }: { copied: boolean }) => void;
};

export const ChatEmailStartButton = ({
  bodyRef,
  email,
  suffix,
  onBodyCopy,
}: ChatEmailStartButtonProps) => {
  const [startedHint, setStartedHint] = useState<string | null>(null);

  useEffect(() => {
    if (!startedHint) return;

    const timer = window.setTimeout(() => {
      setStartedHint(null);
    }, CONFIRMED_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [startedHint]);

  const open = ({ hint }: { hint: string }) => {
    setStartedHint(hint);
    window.open(ChatEmailUtil.toMailtoUrl({ email }));
  };

  const click = () => {
    if (!email.body) {
      open({ hint: TOOLTIPS.noBody.started });

      return;
    }

    const container = bodyRef.current;
    const html = TextareaContentUtil.toHtml({ container });
    const text = TextareaContentUtil.toText({ container }) ?? email.body;

    // Opening a mailto takes focus, and an unfocused document cannot write the clipboard.
    void ClipboardUtil.copy(html ? { html, text } : { text }).then(
      () => {
        onBodyCopy({ copied: true });
        open({ hint: TOOLTIPS.body.started });
      },
      () => {
        onBodyCopy({ copied: false });
        open({ hint: TOOLTIPS.noBody.started });
      }
    );
  };

  const idleHint = email.body ? TOOLTIPS.body.idle : TOOLTIPS.noBody.idle;

  return (
    <Tooltip
      anchor={
        <Button
          alignContent="left"
          icon="envelope"
          iconPosition="left"
          name={`START_EMAIL_${suffix}`}
          onClick={click}
          pattern="primary"
          variation="minimal"
        >
          Send email
        </Button>
      }
      fade
      hideClose
      name={`START_EMAIL_EXPLANATION_${suffix}`}
      position="top"
      variation="minimal"
    >
      {startedHint ?? idleHint}
    </Tooltip>
  );
};
